import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { formatDate } from "../../util/date.ts";

interface DatePickerProps {
  label?: string;
  value?: Date | null;
  onChange?: (date: Date) => void;
  required?: boolean;
  helpText?: string;
  errorText?: string;
  className?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  required,
  helpText,
  errorText,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    value
      ? new Date(value.getFullYear(), value.getMonth(), 1)
      : new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  // Update calendar position when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const calendarWidth = 320; // w-80 = 20rem = 320px
      const calendarHeight = 400; // approximate height
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Use viewport coordinates directly for better mobile compatibility
      let left = rect.left;
      let top = rect.bottom + 4;

      // Prevent horizontal overflow
      if (left + calendarWidth > viewportWidth) {
        left = Math.max(8, viewportWidth - calendarWidth - 8);
      }
      if (left < 8) {
        left = 8;
      }

      // Prevent vertical overflow - show above input if needed
      if (top + calendarHeight > viewportHeight) {
        top = rect.top - calendarHeight - 4;
      }

      setCalendarPosition({ top, left });
    }
  }, [isOpen]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the container (includes the input)
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      // For portaled content, also check if click is inside the calendar element itself
      const calendarElement = (target as Element).closest?.(
        ".fixed.bg-white.border.border-gray-300.rounded-lg.shadow-lg",
      );
      if (calendarElement) {
        return;
      }

      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    onChange?.(newDate);
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const monthYear = currentMonth.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  });

  const isSelectedDate = (day: number) => {
    if (!value) return false;
    return (
      value.getDate() === day &&
      value.getMonth() === currentMonth.getMonth() &&
      value.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  return (
    <div className="mb-3 relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium mb-2 text-gray-700">
          {label}
          {required && <span className="text-danger ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={value ? formatDate(value, "24h", true) : undefined}
          onClick={() => setIsOpen(!isOpen)}
          placeholder="Select a date"
          className={`p-2 rounded border w-full cursor-pointer ${
            errorText ? "border-danger" : "border-gray-300"
          } focus:outline-none focus:ring-2 ${
            errorText ? "focus:ring-danger" : "focus:ring-info"
          } ${className}`}
          required={required}
        />
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      </div>

      {isOpen &&
        createPortal(
          <div
            className="fixed bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-80"
            style={{
              top: `${calendarPosition.top}px`,
              left: `${calendarPosition.left}px`,
              zIndex: 9999,
            }}
          >
            {/* Month/Year header with navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <span className="font-semibold text-gray-700">{monthYear}</span>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-semibold text-gray-500 py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells for days before the first of the month */}
              {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square" />
              ))}

              {/* Actual days */}
              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const selected = isSelectedDate(day);
                const today = isToday(day);

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDateClick(day)}
                    className={`aspect-square flex items-center justify-center rounded text-sm ${
                      selected
                        ? "bg-info text-white font-semibold"
                        : today
                          ? "bg-gray-100 font-semibold text-info"
                          : "hover:bg-gray-50"
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}

      {helpText && !errorText && (
        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
      )}
      {errorText && <p className="text-xs text-danger pt-1">{errorText}</p>}
    </div>
  );
};

export { DatePicker };
